import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/useAuthStore';
import { Button } from '@/components/ui/Button';
import { StorizMark } from '@/components/StorizMark';
import { useT } from '@/lib/i18n';
import NotificationBell from '@/components/NotificationBell';
import { ThemeToggle } from '@/components/appShell/ThemeToggle';
import { usersApi } from '@/lib/api';
import { ADMIN_NAV, activeNavPath } from '@/lib/appShell/navModel';
import { canGoBackInApp } from '@/lib/appShell/gestures';
import { DesktopNav } from '@/components/appShell/DesktopNav';
import { PhoneTabBar } from '@/components/appShell/PhoneTabBar';
import { MoreSheet } from '@/components/appShell/MoreSheet';
import { LangSwitcher } from '@/components/appShell/LangSwitcher';
import { useNavAccess } from '@/components/appShell/useNavAccess';
import { applyAdminAppHead } from '@/lib/pwa/adminHead';
import { registerAdminServiceWorker } from '@/lib/pwa/push';
import { listenForSwNavigate } from '@/lib/pwa/swNavigate';
import { WhatsNew, useWhatsNew } from '@/components/admin/WhatsNew';

interface AdminLayoutProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  actions?: React.ReactNode;
}

export function AdminLayout({ children, title, subtitle, actions }: AdminLayoutProps) {
  // The menu, the bottom tabs and « More » show only pages whose ROUTE lets
  // this person in (lib/appShell/navModel.ts — same rule as ProtectedRoute).
  const access = useNavAccess();
  // Audit: report a session start once (login event in the journal).
  useEffect(() => {
    try {
      if (!sessionStorage.getItem('storiz_login_logged')) {
        sessionStorage.setItem('storiz_login_logged', '1');
        void usersApi.loginEvent().catch(() => {});
      }
    } catch { /* ignore */ }
  }, []);
  const { signOut, profile } = useAuthStore();
  // « Nouveautés » — once per release, reopened from the sidebar (lib/patchNotes.ts).
  const whatsNew = useWhatsNew();
  const { t } = useT();
  const location = useLocation();
  const navigate = useNavigate();
  // « More » closes by itself when the page changes: it is open only for the
  // path it was opened on (derived, no effect).
  const [moreOpenOn, setMoreOpenOn] = useState<string | null>(null);
  const moreOpen = moreOpenOn === location.pathname;
  const setMoreOpen = (open: boolean) => setMoreOpenOn(open ? location.pathname : null);

  // The admin as an app (2026-10-01): the manifest + app tags, the admin
  // service worker for push alerts — registered HERE, with scope /admin/, so
  // customer pages never get it — and the link of a tapped notification when
  // the worker cannot navigate this window itself (/admin is outside /admin/).
  useEffect(() => {
    applyAdminAppHead(document);
    const sw = typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined;
    registerAdminServiceWorker(sw).catch((e: unknown) => console.warn('[pwa] admin service worker not registered', e));
    return listenForSwNavigate(sw, window.location.origin, (path) => navigate(path));
  }, [navigate]);

  // Phone header: a page below a menu section (an order, a customer…) or
  // outside the menu gets a Back button — the hamburger is gone, so the way
  // back must be on screen, not only in the swipe.
  const section = activeNavPath(location.pathname, ADMIN_NAV);
  const showBack = section !== location.pathname;
  const goBack = () => {
    if (canGoBackInApp(window.history.state)) navigate(-1);
    else navigate(section ?? '/admin');
  };

  return (
    <div data-adm-root className="min-h-screen md:flex" style={{ background: 'var(--color-adm-canvas)' }}>
      {whatsNew.open && <WhatsNew release={whatsNew.open} onClose={whatsNew.close} />}
      <aside
        className="hidden md:flex w-64 bg-white flex-col sticky top-0 h-screen shadow-[1px_0_0_rgba(201,162,74,0.18)]"
      >
        <div className="px-5 py-5 border-b flex items-center shrink-0" style={{ borderColor: 'rgba(201, 162, 74, 0.18)' }}>
          {/* text-gray-800 = the default charcoal in light; night mode maps it to the light ink */}
          <span className="text-gray-800 h-7 inline-flex">
            <StorizMark className="h-7" withWordmark tagline="Admin" wordmarkColor="currentColor" taglineColor="color-mix(in srgb, currentColor 50%, transparent)" />
          </span>
        </div>
        <DesktopNav access={access} pathname={location.pathname} />
        <div className="px-3 py-4 border-t shrink-0" style={{ borderColor: 'rgba(201, 162, 74, 0.18)' }}>
          <div className="px-2 pb-2">
            <p className="text-[11px] text-gray-400">{t('Connecté en tant que')}</p>
            <p className="text-xs text-gray-700 truncate font-medium">{profile?.full_name ?? 'Admin'}</p>
          </div>
          <Button variant="ghost" size="sm" className="w-full justify-start" onClick={whatsNew.reopen}>
            ✨ {t('Nouveautés')}
          </Button>
          <Button variant="ghost" size="sm" className="w-full justify-start" onClick={() => signOut()}>
            {t('Déconnexion')}
          </Button>
        </div>
      </aside>

      <main data-adm-main className="flex-1 min-w-0">
        {/* Phone header. Its full height (top safe area + 64 px + 1 px line) is
            --adm-header-h (index.css): change one, change the other. */}
        <header
          className="adm-phone-header md:hidden sticky top-0 z-30"
          style={{
            background: 'var(--color-adm-chrome)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
            borderBottom: '1px solid rgba(201,162,74,0.18)', paddingTop: 'var(--adm-safe-top)',
          }}
        >
          <div className="h-16 px-4 flex items-center gap-2">
            {showBack && (
              <button
                type="button"
                onClick={goBack}
                aria-label={t('Retour')}
                className="w-11 h-11 -ml-3 shrink-0 grid place-items-center rounded-full text-gray-700 active:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A24A]/60"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6" aria-hidden>
                  <path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
            <div className="flex items-center gap-2 min-w-0 flex-1">
              {title ? (
                <h1
                  className="truncate text-[19px] leading-tight text-gray-800"
                  style={{ fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif", fontWeight: 500, letterSpacing: '-0.01em' }}
                >
                  {title}
                </h1>
              ) : (
                <>
                  <span className="text-gray-800 h-5 inline-flex"><StorizMark className="h-5" withWordmark wordmarkColor="currentColor" /></span>
                  <span className="shrink-0 text-[10px] tracking-widest font-semibold px-1.5 py-0.5 rounded-full"
                        style={{ background: 'rgba(201, 162, 74, 0.14)', color: 'var(--color-adm-gold-ink)' }}>
                    ADMIN
                  </span>
                </>
              )}
            </div>
            <div className="ml-auto flex items-center gap-1 shrink-0">
              <ThemeToggle />
              <NotificationBell />
            </div>
          </div>
        </header>

        <header
          className="hidden md:block sticky top-0 z-10"
          style={{ background: 'var(--color-adm-chrome)', backdropFilter: 'blur(8px)', borderBottom: '1px solid rgba(201,162,74,0.18)' }}
        >
          <div className="px-7 py-5 flex items-center justify-between gap-3">
            <div className="min-w-0">
              {title && (
                <h1
                  className="text-2xl truncate text-gray-800"
                  style={{
                    fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
                    fontWeight: 500,
                    letterSpacing: '-0.01em',
                  }}
                >
                  {title}
                </h1>
              )}
              {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
            </div>
            {/* Top-right cluster: page actions + the FR/EN switch (always present) */}
            <div className="flex items-center gap-3 shrink-0">
              {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
              <ThemeToggle />
              <NotificationBell />
              <LangSwitcher />
            </div>
          </div>
        </header>

        {(subtitle || actions) && (
          <div className="md:hidden px-4 py-3 bg-white border-b border-gray-100 flex items-center justify-between gap-3 flex-wrap">
            {subtitle ? <p className="text-sm text-gray-500">{subtitle}</p> : <span />}
            {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
          </div>
        )}

        {/* Bottom padding clears the phone tab bar (--adm-bottom-h is 0 on desktop). */}
        <div
          data-adm-content
          className="px-4 md:px-7 pt-5 md:pt-7"
          style={{ paddingBottom: 'calc(var(--adm-bottom-h) + 20px)' }}
        >
          {children}
        </div>
      </main>

      <PhoneTabBar access={access} pathname={location.pathname} moreOpen={moreOpen} onMore={() => setMoreOpen(!moreOpen)} />
      <MoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} access={access} pathname={location.pathname} />
    </div>
  );
}
